<template>
    <!-- One block, pinned to the bottom by the layout. The CTA is part of it
         rather than part of the page, so there is a single sticky footer rather
         than a page and a layout both reserving viewport height. -->
    <div class="site-footer-block">
        <BlocksToursCta v-if="showCta" />

        <!-- `.cac-global-footer` is the scope every derived selector is prefixed
             with (server/utils/footer/derive.ts). Without this class the
             stylesheet matches nothing, so the class name and FOOTER_SCOPE must
             stay in step. -->
        <div v-if="markup" class="cac-global-footer" v-html="markup" />
    </div>
</template>

<script setup>
    import { useFooterCtaState } from '@/composables/useFooterCta';

    /**
     * architecture.org's footer, embedded on OHC — Asana 1217333501985924.
     *
     * The markup comes from the same standalone page TNEW consumes, and the
     * stylesheet is the derived, scoped copy of that page's CSS. It cannot be
     * inlined raw: the source ships a full reset (`*`, `html`, `body`, `a`, `p`,
     * `h1`-`h6`, and a bare `.container`, which OHC uses as its own layout class
     * on nearly every page), so every selector is prefixed server-side.
     */
    const { data } = await useAsyncData('footer-derived', () => $fetch('/api/footer/derived'), {
        // Chrome, not content: a footer that fails to load must not fail the page.
        default: () => ({ markup: '' }),
    });

    const markup = computed(() => data.value?.markup || '');

    // Raised by pages that want the tours CTA — see useFooterCta().
    const showCta = useFooterCtaState();

    // Linked, not inlined: the sheet is ~84 KB and would otherwise be repeated in
    // every page's HTML. `key` keeps it to a single tag if more than one instance
    // ever mounts.
    useHead({
        link: [{ rel: 'stylesheet', href: '/footer.css', key: 'cac-footer-css' }],
    });
</script>

<style lang="scss">
    // No flex-grow: the shell's #main takes the slack, so this block sits on the
    // bottom of the viewport on short pages and after the content on long ones.
    .site-footer-block {
        flex: 0 0 auto;

        // Air between the end of the page content and the footer. On the block
        // rather than on the CTA inside it, because the CTA is conditional —
        // the neighborhood page hides it in map view (Asana O2-74), and the gap
        // went with it. A flex item, so this margin cannot collapse away.
        margin-top: 20px;
    }

    // The CTA's red runs straight into the footer's red newsletter banner — same
    // #cc0000 — so the two read as one mass. That has been through three
    // settings: a 4px divider, then 2px (2026-08-26), then 40/64px of real white
    // space when the hairline was judged too faint to register (Asana O2-62).
    // Back to a 2px gap on 2026-09-06 — the two blocks butting together is the
    // intended look, with just enough of a seam to keep them distinct.
    //
    // A positive margin, not a negative one: the whole gap was this rule's own
    // margin-bottom. The .cac-global-footer that follows contributes nothing —
    // 0 margin-top and 0 padding-top, measured — so there is no outside spacing
    // to pull back through, and a negative value here would overlap the footer
    // rather than sit against it.
    //
    // Scoped to the footer block, and two classes so it beats tours-cta.vue's own
    // `.tours-cta` (0,1,0) regardless of stylesheet order.
    .site-footer-block .tours-cta {
        margin-bottom: 2px;
    }

    // architecture.org caps its container at 1360px; OHC caps content at $hd
    // (1150px), so the footer's edges sat 105px wider than everything above it.
    //
    // Overridden here rather than rewritten in the derive step so the value stays
    // tied to OHC's own token — if $hd moves, the footer follows.
    //
    // THREE classes deliberately: the derived rule is `.cac-global-footer
    // .container` (0,2,0), and a matching-specificity override would depend on
    // which stylesheet the browser happened to order last. The extra class makes
    // it (0,3,0) and therefore deterministic.
    .site-footer-block .cac-global-footer .container {
        // architecture.org: `max-width: 1360px; width: 90vw`.
        // OHC content: capped at $hd, full width below that, 16px gutter on phones.
        //
        // Capping alone was not enough. Because 90vw only reaches 1150px at a
        // ~1278px viewport, the footer sat 35px inside the content between 1150
        // and 1278, and 9px inside it on phones. Matching the WIDTH as well as
        // the cap is what actually lines the edges up at every size.
        max-width: $hd;
        width: 100%;
        padding-left: 16px;
        padding-right: 16px;

        // Dropped at $hd + 32px, NOT at $tablet. The cap is $hd, so between
        // 768 and 1150 the container is simply full width — removing the
        // padding there left the footer flush to the window edges while
        // everything above it kept a 16px gutter, and the social row and the
        // last link column ran off the right. The gutter can only go once the
        // viewport is wide enough to provide one on its own, which is the cap
        // plus both gutters. Same rule and same reasoning as .hd-container in
        // assets/styles/container.scss — the two have to agree or the footer
        // and the page disagree in whichever band they differ.
        @media (min-width: $hd + 32px) {
            padding-left: 0;
            padding-right: 0;
        }
    }
</style>
